import { chain, each, filter } from 'lodash'
import dayjs from '@/dayjs'
import dateLocale from '@/constants/dateLocale'
import { useFilters } from '@/composables/filters'
import { useVersionStore } from '@/stores'

export function useCalendarHelpers() {
    const VIEW_MONTH = 'month'
    const VIEW_WEEK = 'week'
    const VIEW_DAY = 'day'

    function getSchoolLocale() {
        return (useVersionStore().version?.school_locale ?? '').replace('_', '-')
    }

    function isUSLocale() {
        return getSchoolLocale() === dateLocale.en_US
    }

    function filterEvents(events, rangeStart, rangeEnd, filtersById, activeFilters) {
        const { applyLocalFilter } = useFilters()
        return filter(events, (event) => {
            // Only show events which overlap the selected time period
            const inRange = dayjs(event.end).isSameOrAfter(rangeStart, 'day')
                && dayjs(event.start).isSameOrBefore(rangeEnd, 'day')

            if (!inRange) {
                return false
            }

            // Check if event tags matches filters
            return applyLocalFilter(filtersById, activeFilters, event.tags)
        })
    }

    /**
     * @typedef {Object} EventLikeObject
     * @property {string} start - The start date as a string.
     * @property {string} end - The end date as a string.
     * @property {boolean} all_day - Whether the event is all-day.
     */

    /**
     * @typedef {Object} CalendarEventObject
     * @extends EventLikeObject
     * @property {string} id - ID of the event.
     * @property {string|null} read_url - Read URL of the event.
     * @property {Object|null} link - Link for the event.
     */

    /**
     * @template {EventLikeObject} T - The type of source event data.
     * @typedef {Object} MultiDayEvent
     * @property {dayjs.Dayjs} day - Start of the day this item is on.
     * @property {dayjs.Dayjs} startForDay - Time the event starts on this day.
     * @property {dayjs.Dayjs} endForDay - Time the event ends on this day.
     * @property {boolean} allDay - Whether the event was marked as an all-day event.
     * @property {boolean} effectivelyAllDay - Whether the event was marked as an all-day event OR the event runs over the entire day.
     * @property {dayjs.Dayjs} start - Time the event starts.
     * @property {dayjs.Dayjs} end - Time the event ends.
     * @property {T} event - The original event object this item was generated from.
     */

    /**
     * @template {EventLikeObject} T - The type of source event data.
     * @param {T[]} events - Array of event-like objects (see type definition for properties).
     * @param {dayjs.Dayjs} rangeStart - The start of the date range to filter events.
     * @param {dayjs.Dayjs} rangeEnd - The end of the date range to filter events.
     * @return {MultiDayEvent<T>[]} Array of items representing each day an event occurs on, with start and end times for that day.
     */
    function generateMultiDayEvents(events, rangeStart, rangeEnd) {
        const result = []

        each(events, (event) => {
            // For all-day events, expand to full days
            const eventStart = event.all_day
                ? dayjs(event.start).startOf('day')
                : dayjs(event.start)

            const eventEnd = event.all_day
                ? dayjs(event.end).endOf('day')
                : dayjs(event.end)

            // Skip events outside the range
            if (eventEnd.isBefore(rangeStart) || eventStart.isAfter(rangeEnd)) {
                return
            }

            // Clamp event start and end to the range
            const start = dayjs.max(eventStart, rangeStart)
            const end = dayjs.min(eventEnd, rangeEnd)

            let currentDay = start.startOf('day')

            while (currentDay.isBefore(end)) {
                const allDay = event.all_day ?? false
                const dayStart = currentDay
                const dayEnd = currentDay.endOf('day')

                const segmentStart = event.all_day
                    ? dayStart
                    : dayjs.max(start, dayStart)
                const segmentEnd = event.all_day
                    ? dayEnd
                    : dayjs.min(end, dayEnd)

                if (segmentStart.isSameOrBefore(segmentEnd)) {
                    result.push({
                        day: dayStart,
                        startForDay: segmentStart,
                        endForDay: segmentEnd,
                        allDay: allDay,
                        // Specify the comparison unit as seconds (expected precision from integrations), otherwise the check may fail as dayEnd includes milliseconds
                        effectivelyAllDay: allDay || (segmentStart.isSame(dayStart, 'second') && segmentEnd.isSame(dayEnd, 'second')),
                        start: eventStart,
                        end: eventEnd,
                        event,
                    })
                }

                currentDay = currentDay.add(1, 'day')
            }
        })

        return result
    }

    /**
     * @param {dayjs.Dayjs} now
     * @param {CalendarEventObject[]} events - Array of objects with start, end and all_day properties.
     * @param {dayjs.Dayjs} rangeStart - The start of the date range to filter events.
     * @param {dayjs.Dayjs} rangeEnd - The end of the date range to filter events.
     * @param {function(MultiDayEvent<CalendarEventObject>): any} additionalSort - Optional additional sorting function.
     */
    function generateMultiDayEventsAsItems(now, events, rangeStart, rangeEnd, additionalSort) {
        return chain(generateMultiDayEvents(events, rangeStart, rangeEnd))
            .orderBy([
                (item) => {
                    return item.startForDay.unix()
                },
                (item) => {
                    // Move all-day events above events than span multiple days (they also start at the beginning of the day)
                    return item.allDay ? 0 : 1
                },
                ...(additionalSort ? [additionalSort] : []),
            ])
            .map(({ event, day }) => {
                const isCurrentYear = day.isSame(now, 'year')
                let headingDateFormat = isUSLocale() ? 'dddd, MMMM D' : 'dddd, D MMMM'
                if (!isCurrentYear) {
                    headingDateFormat += ' YYYY'
                }

                return {
                    heading: day.format(headingDateFormat),
                    date: day.format('YYYY-MM-DD'),
                    meta: { id: event.id, read: event.read_url },
                    data: event,
                    link: event.link,
                }
            })
            .value()
    }

    /**
     * @param {EventLikeObject[]} events - Array of objects with start, end and all_day properties.
     * @param {dayjs.Dayjs} rangeStart - The start of the date range to filter events.
     * @param {dayjs.Dayjs} rangeEnd - The end of the date range to filter events.
     * @param {function(MultiDayEvent): any} additionalSort - Optional additional sorting function.
     */
    function generateMultiDayEventsForToday(events, rangeStart, rangeEnd, additionalSort) {
        return chain(generateMultiDayEvents(events, rangeStart, rangeEnd))
            .orderBy([
                (item) => {
                    return item.startForDay.unix()
                },
                (item) => {
                    // Move all-day events above events than span multiple days (they also start at the beginning of the day)
                    return item.allDay ? 0 : 1
                },
                ...(additionalSort ? [additionalSort] : []),
            ])
            .value()
    }

    function generateUpcomingMultiDayEventsForToday(now, events, rangeStart, rangeEnd, additionalSort) {
        return filter(
            generateMultiDayEventsForToday(events, rangeStart, rangeEnd, additionalSort),
            (item) => {
                if (item.allDay || !item.day.isSame(now, 'day')) {
                    return true
                }
                // Show events that are still ongoing or start later today
                return item.endForDay.isAfter(now)
            },
        )
    }

    function getStartForView(view, start) {
        if (view === VIEW_MONTH) {
            return dayjs(start).startOf('month').startOf('week')
        }
        if (view === VIEW_WEEK) {
            return dayjs(start).startOf('week')
        }
        if (view === VIEW_DAY) {
            return dayjs(start).startOf('day')
        }
    }

    function getEndForView(view, start) {
        if (view === VIEW_MONTH) {
            return dayjs(start).endOf('month').endOf('week')
        }
        if (view === VIEW_WEEK) {
            return dayjs(start).endOf('week')
        }
        if (view === VIEW_DAY) {
            return dayjs(start).endOf('day')
        }
    }

    function getDateForNewMonth(currentDate, newMonth, newYear) {
        // When changing to current month, select today, otherwise start of month
        const newDate = dayjs().set('month', newMonth).set('year', newYear).startOf('month')
        if (newDate.month() !== dayjs(currentDate).month()) {
            if (newDate.month() === dayjs().month() && newDate.year() === dayjs().year()) {
                // go to today if current month and year
                return dayjs().format('YYYY-MM-DD')
            }
            return newDate.format('YYYY-MM-DD')
        }
        // Same month, don't change date
        return currentDate
    }

    return {
        VIEW_MONTH,
        VIEW_WEEK,
        VIEW_DAY,
        filterEvents,
        generateMultiDayEvents,
        generateMultiDayEventsForToday,
        generateUpcomingMultiDayEventsForToday,
        generateMultiDayEventsAsItems,
        getStartForView,
        getEndForView,
        getDateForNewMonth,
        getSchoolLocale,
    }
}
